import { Link, NavLink } from "react-router-dom";
import {
  ShoppingCart,
  Package,
  Menu,
  X,
  Home,
  Truck,
  Phone,
  Mail,
  Recycle,
  ShieldCheck,
  Scissors,
  Layers,
  ChevronRight,
} from "lucide-react";
import { useCart } from "@/context/CartContext";
import CartSheet from "@/components/CartSheet";
import { useState } from "react";

const MOBILE_CATEGORIES = [
  { label: "Cartons de déménagement", to: "/cartons-demenagement", Icon: Package },
  { label: "Cartons reconditionnés", to: "/cartons-reconditionnes", Icon: Recycle },
  { label: "Emballage et protection", to: "/emballage-protection", Icon: ShieldCheck },
  { label: "Fermeture et accessoires", to: "/fermeture-accessoires", Icon: Scissors },
  { label: "Kit cartons déménagement", to: "/packs-cartons", Icon: Layers },
];

const Navbar = () => {
  const { totals, setOpen } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);

  const linkClass = ({ isActive }) =>
    `text-sm font-medium transition-colors hover:text-[#237cb5] ${
      isActive ? "text-[#237cb5]" : "text-[#0f1d2b]"
    }`;

  const closeMenu = () => setMobileOpen(false);

  return (
    <>
      <header
        className="sticky top-0 z-40 w-full backdrop-blur-xl bg-white/80 border-b border-[#e6edf3]"
        data-testid="site-navbar"
      >
        <div className="container-cdm flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo-link">
            <div className="h-9 w-9 bg-[#237cb5] flex items-center justify-center rounded-sm">
              <Package className="h-5 w-5 text-white" strokeWidth={2.5} />
            </div>
            <div className="leading-none">
              <div className="font-display font-extrabold text-xl  uppercase">
                carton<span className="text-[#237cb5]">move</span>
              </div>
              <div className="overline mt-1 hidden sm:block">Livraison Île-de-France</div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            <NavLink to="/" end className={linkClass} data-testid="nav-home">Accueil</NavLink>
            <NavLink to="/cartons-demenagement" className={linkClass} data-testid="nav-shop">Boutique</NavLink>
            <a href="/#livraison" className="text-sm font-medium text-[#0f1d2b] hover:text-[#237cb5]" data-testid="nav-delivery">Livraison</a>
            <a href="/#contact" className="text-sm font-medium text-[#0f1d2b] hover:text-[#237cb5]" data-testid="nav-contact">Contact</a>
          </nav>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setOpen(true)}
              className="relative inline-flex items-center justify-center h-10 w-10 border border-zinc-900 hover:bg-zinc-900 hover:text-white transition-colors rounded-sm"
              data-testid="cart-icon-button"
              aria-label="Ouvrir le panier"
            >
              <ShoppingCart className="h-4 w-4" />
              {totals.count > 0 && (
                <span
                  className="absolute -top-2 -right-2 h-5 min-w-[20px] px-1 bg-[#237cb5] text-white text-xs font-bold rounded-full flex items-center justify-center"
                  data-testid="cart-badge"
                >
                  {totals.count}
                </span>
              )}
            </button>
            <button
              onClick={() => setMobileOpen(true)}
              className="md:hidden h-10 w-10 inline-flex items-center justify-center border border-[#e6edf3] rounded-sm"
              data-testid="mobile-menu-open"
              aria-label="Menu"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-50 bg-white md:hidden flex flex-col"
          data-testid="mobile-menu"
        >
          {/* Top bar */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-[#e6edf3] shrink-0">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-2"
            >
              <div className="h-8 w-8 bg-[#237cb5] flex items-center justify-center rounded-sm">
                <Package className="h-4 w-4 text-white" strokeWidth={2.5} />
              </div>
              <span className="font-display font-extrabold text-lg uppercase">
                carton<span className="text-[#237cb5]">move</span>
              </span>
            </Link>
            <button
              onClick={() => setMobileOpen(false)}
              aria-label="Fermer"
              data-testid="mobile-menu-close"
              className="h-10 w-10 inline-flex items-center justify-center rounded-lg border border-[#e6edf3] text-[#0f1d2b] active:bg-[#f7fafc] [-webkit-tap-highlight-color:transparent]"
            >
              <X className="h-4 w-4" strokeWidth={2.5} />
            </button>
          </div>

          {/* Scrollable content */}
          <nav className="flex-1 overflow-y-auto px-4 pt-4 pb-8 space-y-6">
            {/* Section: Navigation */}
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#5b6b7a] px-1 mb-2">
                Navigation
              </div>
              <div className="rounded-2xl border border-[#e6edf3] bg-white overflow-hidden">
                {[
                  { label: "Accueil", to: "/", Icon: Home, testId: "mobile-nav-home", external: false },
                  { label: "Livraison", to: "/#livraison", Icon: Truck, testId: "mobile-nav-delivery", external: true },
                  { label: "Contact", to: "/#contact", Icon: Mail, testId: "mobile-nav-contact", external: true },
                ].map(({ label, to, Icon, testId, external }, idx, arr) => {
                  const className = `w-full flex items-center justify-between gap-3 px-3.5 py-3 text-left text-[14px] font-bold text-[#0f1d2b] active:bg-[#f7fafc] [-webkit-tap-highlight-color:transparent] ${
                    idx !== arr.length - 1 ? "border-b border-[#eef2f6]" : ""
                  }`;
                  const content = (
                    <>
                      <span className="inline-flex items-center gap-3 min-w-0">
                        <span className="flex items-center justify-center h-9 w-9 rounded-full bg-[#eaf3fb] text-[#237cb5] shrink-0">
                          <Icon className="h-4 w-4" strokeWidth={2.4} />
                        </span>
                        <span className="truncate">{label}</span>
                      </span>
                      <ChevronRight className="h-4 w-4 text-[#5b6b7a] shrink-0" strokeWidth={2.5} />
                    </>
                  );
                  return external ? (
                    <a key={label} href={to} onClick={closeMenu} data-testid={testId} className={className}>
                      {content}
                    </a>
                  ) : (
                    <Link key={label} to={to} onClick={closeMenu} data-testid={testId} className={className}>
                      {content}
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* Section: Boutique */}
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#5b6b7a] px-1 mb-2">
                Boutique
              </div>
              <div className="rounded-2xl border border-[#e6edf3] bg-white overflow-hidden">
                {MOBILE_CATEGORIES.map(({ label, to, Icon }, idx, arr) => {
                  const testId = `mobile-cat-${label.toLowerCase().replace(/\s+/g, "-")}`;
                  return (
                    <Link
                      key={label}
                      to={to}
                      onClick={closeMenu}
                      data-testid={testId}
                      className={`flex items-center justify-between gap-3 px-3.5 py-3 text-[14px] font-bold text-[#0f1d2b] active:bg-[#f7fafc] [-webkit-tap-highlight-color:transparent] ${
                        idx !== arr.length - 1 ? "border-b border-[#eef2f6]" : ""
                      }`}
                    >
                      <span className="inline-flex items-center gap-3 min-w-0">
                        <span className="flex items-center justify-center h-9 w-9 rounded-full bg-[#eaf3fb] text-[#237cb5] shrink-0">
                          <Icon className="h-4 w-4" strokeWidth={2.4} />
                        </span>
                        <span className="truncate">{label}</span>
                      </span>
                      <ChevronRight className="h-4 w-4 text-[#5b6b7a] shrink-0" strokeWidth={2.5} />
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* Section: Contact direct */}
            <div className="rounded-2xl bg-[#EEF3F7] border border-[#cfdbe6] p-4">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#5b6b7a] mb-2">
                Une question ?
              </div>
              <a
                href="tel:0123456789"
                className="inline-flex items-center gap-2 text-[14px] font-extrabold text-[#0f1d2b] [-webkit-tap-highlight-color:transparent]"
                data-testid="mobile-phone"
              >
                <Phone className="h-4 w-4 text-[#237cb5]" strokeWidth={2.4} />
                01 23 45 67 89
              </a>
              <div className="mt-1.5 text-[12px] text-[#5b6b7a]">
                Livraison 24-48 h en Île-de-France
              </div>
            </div>
          </nav>
        </div>
      )}

      <CartSheet />
    </>
  );
};

export default Navbar;
